import { AfterViewInit, ContentChildren, Directive, EventEmitter, Input, OnDestroy, Output, QueryList, forwardRef } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subject, combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { KeyboardItemDirective } from './keyboard-item.directive';

/**
 * Eine Direktive, die die Tastatursteuerung für eine Liste ermöglicht.
 *
 * Funktioniert min folgenden Direktiven:
 * - KeyboardItemDirective - definiert ein Item-Element (Pflichtelement).
 * - KeyboardArrowsDirective - ermöglicht Oben - Unten Tastatur-Steuerung
 * - KeyboardSymbolSearchDirective - ermöglicht Suche nach einem Symbol
 *
 *
 * Beispiel:
 * <ul [commonKeyboardList]="true" commonKeyboardListNavArrow commonKeyboardListNavTab commonKeyboardSymbolSearch
 *      (commonKeyboardListThresholdTop)="onKeyboardBlur()">
 *     <li commonKeyboardItem>Item 1</li>
 *     <li commonKeyboardItem>Item 2</li>
 *     <li commonKeyboardItem>Item 3</li>
 * </ul>
 */
@Directive({
    selector: '[commonKeyboardList]',
    exportAs: 'commonKeyboardList'
})
export class KeyboardListDirective<T> implements OnDestroy, AfterViewInit {

    // https://stackoverflow.com/questions/37554729/contentchildren-is-not-being-populated-when-parent-render-items-using-ng-conten
    @ContentChildren(forwardRef(() => KeyboardItemDirective), { descendants: true })
    items: QueryList<KeyboardItemDirective<T>>;

    @Output('commonKeyboardListThresholdTop') blur$: EventEmitter<void> = new EventEmitter<void>();

    @Input('commonKeyboardList')
    set enable(val: boolean | null | string) {
        if (val === null || val === '') {
            return;
        }

        if (!!val !== this.enabled$.getValue()) {
            this.enabled$.next(!!val);
        }
    }

    /**
     * Will be used by KeyboardListNavigationDirective.
     */
    enabled$ = new BehaviorSubject(true);

    private ngUnsubscribe$: Subject<void> = new Subject<void>();

    private selectedItemId$ = new ReplaySubject<number>();

    /**
     * Aktueller Index. Wird für KeyboardArrowsDirective und KeyboardSymbolSearchDirective verwendet.
     */
    private selectedIndex: number = -1;

    @Input('commonKeyboardListSelectedItemId')
    set selectedItemId(itemId: number) {
        this.selectedItemId$.next(itemId);
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.selectedItemId$.complete();
    }

    ngAfterViewInit(): void {
        const items$ = this.items.changes.pipe(
            startWith(this.items),
            filter(items => items.length > 0),
            map(items => items.toArray())
        );

        // Fokussieren bei den jeweils items oder selectedItemId Änderung.
        //
        // Für Form-Select ist es wichtig bei der Initialisierung der Komponenten ein Item zu fokussieren,
        //    da ohne dies die Tasksteuerung nicht aktiv wird, weil die Liste in CDK Overlay dargestellt wird.
        combineLatest([items$, this.selectedItemId$]).pipe(
            map(([items, itemId]) => items.findIndex(item => item.commonKeyboardItemId === itemId)),
            filter(index => index > -1)
        ).subscribe(index => setTimeout(() => this.moveTo(index)));
    }

    moveTo(index: number): void {
        if (index <= -1) {
            return this.close();
        }
        if (index > this.items.length - 1) {
            return;
        }
        this.items.toArray()[index].focus();
        this.selectedIndex = index;
    }

    moveToFirst(): void {
        this.moveTo(0);
    }

    getSelectedIndex(): number {
        return this.selectedIndex;
    }

    private close() {
        this.blur$.next();
        this.selectedIndex = -1;
    }
}
